import { a as FexiosError, F as FexiosErrorCodes, b as FexiosResponseError } from './fexios.B-14qd8n.mjs';

class FexiosResponse {
  constructor(rawResponse, data, responseType) {
    this.rawResponse = rawResponse;
    this.data = data;
    this.responseType = responseType;
    ["ok", "status", "statusText", "headers", "url", "redirected"].forEach(
      (key) => {
        Reflect.defineProperty(this, key, {
          get: () => rawResponse[key]
        });
      }
    );
  }
  ok;
  status;
  statusText;
  headers;
  url;
  redirected;
}
const concatUint8Arrays = (parts) => {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
};
async function readBody(stream, contentLength, onProgress) {
  const reader = stream.getReader();
  if (!reader) {
    throw new FexiosError(
      FexiosErrorCodes.NO_BODY_READER,
      "Failed to get ReadableStream from response body"
    );
  }
  const chunks = [];
  let received = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (onProgress && contentLength > 0)
        onProgress(received / contentLength, concatUint8Arrays(chunks));
    }
  } finally {
    reader.releaseLock?.();
  }
  const data = concatUint8Arrays(chunks);
  onProgress?.(1, data);
  return data;
}
const guessFexiosResponseType = (contentType) => {
  if (!contentType) return void 0;
  if (contentType.includes("application/json") || contentType.endsWith("+json"))
    return "json";
  if (contentType.startsWith("text/")) return "text";
  if (contentType.includes("multipart/form-data") || contentType.includes("application/x-www-form-urlencoded"))
    return "form";
  if (/^image\//.test(contentType) || /^video\//.test(contentType) || /^audio\//.test(contentType) || contentType.includes("application/pdf"))
    return "blob";
  if (contentType.includes("application/octet-stream") || contentType.includes("application/zip") || contentType.includes("application/x-tar") || contentType.includes("application/x-7z-compressed") || contentType.includes("application/x-gzip"))
    return "arrayBuffer";
  return void 0;
};
async function createFexiosResponse(rawResponse, expectedType, onProgress, shouldThrow, timeout) {
  const decodeResponse = rawResponse.clone();
  const contentType = rawResponse.headers.get("content-type")?.toLowerCase() ?? "";
  const lenHeader = rawResponse.headers.get("content-length");
  const total = lenHeader ? Number(lenHeader) : 0;
  const upgrade = rawResponse.headers.get("upgrade")?.toLowerCase();
  const connection = rawResponse.headers.get("connection")?.toLowerCase();
  if (expectedType === "ws" || expectedType === "stream") {
    throw new FexiosError(
      FexiosErrorCodes.FEATURE_MOVED_TO_PLUGIN,
      `responseType "${String(
        expectedType
      )}" has been moved to plugins. Use "fexios/plugins" (fx.plugin(pluginWebSocket) / fx.plugin(pluginSSE)) and call fx.ws()/fx.sse() instead.`
    );
  }
  if (upgrade === "websocket" && connection === "upgrade") {
    throw new FexiosError(
      FexiosErrorCodes.FEATURE_MOVED_TO_PLUGIN,
      `WebSocket upgrade response detected. WebSocket support has been moved to plugins. Please use "fexios/plugins" and call fx.ws().`
    );
  }
  if (contentType.includes("text/event-stream")) {
    throw new FexiosError(
      FexiosErrorCodes.FEATURE_MOVED_TO_PLUGIN,
      `SSE (text/event-stream) response detected. SSE support has been moved to plugins. Please use "fexios/plugins" and call fx.sse().`
    );
  }
  let resolvedType = expectedType ?? guessFexiosResponseType(contentType) ?? "text";
  const charset = /\bcharset=([^;]+)/i.exec(contentType)?.[1]?.trim() || "utf-8";
  const decoder = new TextDecoder(charset);
  let data;
  try {
    if (resolvedType === "form") {
      data = await decodeResponse.formData();
    } else {
      const bytes = await readBody(decodeResponse.body, total, onProgress);
      if (resolvedType === "arrayBuffer") {
        data = bytes.buffer.slice(
          bytes.byteOffset,
          bytes.byteOffset + bytes.byteLength
        );
      } else if (resolvedType === "blob") {
        data = new Blob([bytes], {
          type: contentType || "application/octet-stream"
        });
      } else if (resolvedType === "text") {
        const text = decoder.decode(bytes);
        if (!expectedType) {
          const trimmed = text.trim();
          if (trimmed.startsWith("{") && trimmed.endsWith("}") || trimmed.startsWith("[") && trimmed.endsWith("]")) {
            try {
              data = JSON.parse(trimmed);
              resolvedType = "json";
            } catch {
              data = text;
            }
          } else {
            data = text;
          }
        } else {
          data = text;
        }
      } else if (resolvedType === "json") {
        const text = decoder.decode(bytes);
        data = text.length ? JSON.parse(text) : null;
      } else {
        data = bytes;
      }
    }
  } catch (e) {
    if (!(e instanceof Error)) throw e;
    try {
      const t = await decodeResponse.text();
      data = t;
      resolvedType = "text";
    } catch {
      throw new FexiosError(
        FexiosErrorCodes.BODY_TRANSFORM_ERROR,
        `Failed to transform response body to ${resolvedType}`,
        void 0,
        { cause: e }
      );
    }
  }
  const response = new FexiosResponse(
    rawResponse,
    data,
    resolvedType
  );
  const decision = shouldThrow?.(response);
  if (typeof decision === "boolean" ? decision : !response.ok) {
    throw new FexiosResponseError(response.statusText, response);
  }
  return response;
}

export { FexiosResponse as F, createFexiosResponse as c };
//# sourceMappingURL=fexios.DFl5Gol9.mjs.map
